import React from 'react';
import { Col, Collapse, Row } from 'reactstrap';

import { deleteCustomPlaylist } from '../../services/customAudioPlaylistService';
import {
  getSessionStorageItem,
  removeSessionStorageItem,
} from '../../utils/sessionStorage';
import CircularLoading from '../Common/CircularLoading/CircularLoading';
import PfeSktSwitch from '../Common/PfeSktSwitch/PfeSktSwitch';
import { ModalNoReferences } from '../ManageSubscription/ModalNoReferences';
import AudioPlayer from '../StudyList/Audio/AudioPlayer';
import AudioBadge from '../StudyList/Badges/AudioBadge';
import AudioTypeSelector from './AudioTypeSelector';
import CreatePlaylistButton from './CreatePlaylistButton';
import CreatePlaylistModal from './CreatePlaylistModal';
import CustomPlaylistSection from './CustomPlaylistSection';
import DeletePlaylistModal from './DeletePlaylistModal';

// -1 is used as the "intro/summary" index in AudioPlayer.jsx
const INTRO_AUDIO_IDX = -1;

class AudioStudyView extends React.Component {
  constructor(props) {
    super(props);

    const highlightPSPlaylist =
      getSessionStorageItem('fromPersonalizedStudy') === 'true';
    removeSessionStorageItem('fromPersonalizedStudy');

    this.state = {
      audioTypeFilter: 'qa',
      isOpenAudioPlayer: false,
      chapterNameForAudioPlaylist: null,
      customPlaylistChapters: null,
      editingPlaylist: null,

      audioChapters: [],
      openRefIds: {},

      hasFetchedReferences: false,
      hasLoadedAudioOnce: false,
      showNoReferencesModal: false,

      showCreatePlaylistModal: false,
      showUpdateSuccess: false,

      deleteModalOpen: false,
      playlistToDelete: null,
      deletingPlaylist: false,
      highlightPSPlaylist,
    };
  }

  componentDidMount() {
    this.ensureCurrentIdAndFetch();
  }

  componentDidUpdate(prevProps) {
    // If rate changed => reset flags and trigger new fetch
    if (prevProps.pfeSktId !== this.props.pfeSktId && this.props.pfeSktId) {
      this.setState({
        hasFetchedReferences: false,
        hasLoadedAudioOnce: false,
        showNoReferencesModal: false,

        audioChapters: [],
        openRefIds: {},

        // close player
        isOpenAudioPlayer: false,
        chapterNameForAudioPlaylist: null,
      });

      this.props.getContentByReference(this.props.pfeSktId);
      this.props.getCustomPlaylists();
      return;
    }

    // When fetch finished: decide modal vs content
    if (prevProps.loadingReferences && !this.props.loadingReferences) {
      const refs = this.getReferencesFromProps();
      const hasRefs = Array.isArray(refs) && refs.length > 0;

      this.setState({
        hasFetchedReferences: true,
        hasLoadedAudioOnce: true,
        showNoReferencesModal: !hasRefs,
      });

      if (!hasRefs) {
        this.setState({
          audioChapters: [],
          openRefIds: {},
        });
        return;
      }

      // Build filtered list and request progress
      this.rebuildAudioChaptersList(refs);
      const chapterIds = this.extractAudioChapterIds(refs);
      if (chapterIds.length) this.props.getAudioPlaylistProgress(chapterIds);
    }

    if (
      this.state.highlightPSPlaylist &&
      !this.state.hasAutoSelectedPSTab &&
      this.state.hasFetchedReferences &&
      Array.isArray(this.props.customPlaylists) &&
      !this.props.customPlaylistsLoading
    ) {
      this.autoSelectTabForPSPlaylist();
    }
  }

  autoSelectTabForPSPlaylist = () => {
    const PS_PLAYLIST_BASE = 'Personalized Study Automatic Playlist';
    const psPlaylist = this.props.customPlaylists.find((p) =>
      p.name.startsWith(PS_PLAYLIST_BASE),
    );

    if (!psPlaylist) {
      this.setState({ hasAutoSelectedPSTab: true });
      return;
    }

    const chapterIds = new Set(
      (psPlaylist.chapters || []).map((c) => Number(c.chapterId)),
    );
    const psChapters = this.state.audioChapters.filter((ch) =>
      chapterIds.has(Number(ch.id)),
    );

    // audioChapters not yet loaded for the current rate — wait for next update
    if (psPlaylist.chapters?.length > 0 && psChapters.length === 0) return;

    this.setState({ hasAutoSelectedPSTab: true });
    const hasQA = psChapters.some((ch) => ch.hasPlaylist);
    if (!hasQA) {
      this.setState({ audioTypeFilter: 'summary' });
    }
  };

  openCreatePlaylistModal = () => {
    this.setState({ showCreatePlaylistModal: true });
  };

  closeCreatePlaylistModal = () => {
    this.setState({
      showCreatePlaylistModal: false,
      editingPlaylist: null,
    });
  };

  handlePlaylistCreated = () => {
    this.props.getCustomPlaylists();
    this.setState({
      showCreatePlaylistModal: false,
      // Only show success message when editing (update), not on create
      showUpdateSuccess: !!this.state.editingPlaylist,
      editingPlaylist: null,
    });

    if (this.state.editingPlaylist) {
      setTimeout(() => {
        this.setState({ showUpdateSuccess: false });
      }, 3000);
    }
  };

  handleOpenDeleteModal = (playlist) => {
    this.setState({
      deleteModalOpen: true,
      playlistToDelete: playlist,
    });
  };

  handleCloseDeleteModal = () => {
    if (this.state.deletingPlaylist) return;

    this.setState({
      deleteModalOpen: false,
      playlistToDelete: null,
    });
  };

  handleConfirmDelete = async () => {
    const { playlistToDelete } = this.state;

    if (!playlistToDelete?.id) return;

    this.setState({ deletingPlaylist: true });

    try {
      await deleteCustomPlaylist(playlistToDelete.id);

      this.props.getCustomPlaylists();

      this.setState({
        deleteModalOpen: false,
        playlistToDelete: null,
        deletingPlaylist: false,
      });
    } catch (err) {
      this.setState({ deletingPlaylist: false });
    }
  };

  getReferencesFromProps = () => {
    if (Array.isArray(this.props.references)) return this.props.references;
    if (Array.isArray(this.props.items?.references))
      return this.props.items.references;
    return [];
  };

  ensureCurrentIdAndFetch = () => {
    const {
      user,
      pfeSktId,
      setSwitchPfeSkt,
      getContentByReference,
      getCustomPlaylists,
      references,
      audioPlaylistsProgress,
    } = this.props;

    if (!user) return;

    getCustomPlaylists();

    if (pfeSktId) {
      // If coming from Personalized Study, always do a fresh fetch to ensure
      // audioChapters are loaded for the correct rate (cache may be stale).
      if (this.state.highlightPSPlaylist) {
        getContentByReference(pfeSktId);
        return;
      }

      // If references are already in the store for this pfeSktId,
      // skip the API call and build the list directly from existing data.
      if (references && references.length > 0) {
        this.rebuildAudioChaptersList(references);
        const chapterIds = this.extractAudioChapterIds(references);

        // Only fetch progress for chapters not already in the store
        const missingIds = chapterIds.filter(
          (id) => !audioPlaylistsProgress?.[id],
        );
        if (missingIds.length) this.props.getAudioPlaylistProgress(missingIds);

        this.setState({
          hasFetchedReferences: true,
          hasLoadedAudioOnce: true,
          showNoReferencesModal: false,
        });

        return;
      }

      // Store is empty: fetch from API
      getContentByReference(pfeSktId);
      return;
    }

    const fallbackId = user?.pfeRate?.id || user?.rate?.id;
    if (!fallbackId) return;

    setSwitchPfeSkt(fallbackId);
    getContentByReference(fallbackId);
  };

  getActiveTab = () => {
    const { pfeSktId, user } = this.props;

    if (user?.rate?.isPfeOnly) {
      return 'PFE';
    }

    if (
      pfeSktId &&
      user?.rate?.id &&
      Number(pfeSktId) === Number(user.rate.id)
    ) {
      return 'SKT';
    }

    return 'PFE';
  };

  handleTabSwitch = (tab) => {
    const { user, setSwitchPfeSkt } = this.props;
    if (!user) return;

    if (tab === 'SKT' && user.rate?.isPfeOnly) {
      return;
    }

    if (tab === 'SKT') {
      if (user?.rate?.id) setSwitchPfeSkt(user.rate.id);
    } else {
      if (user?.pfeRate?.id) setSwitchPfeSkt(user.pfeRate.id);
    }
  };

  // Build filtered audio chapters
  extractAudioChapterIds = (references = []) => {
    if (!Array.isArray(references)) return [];
    const ids = [];

    references.forEach((ref) => {
      (ref.chapters || []).forEach((ch) => {
        if (ch?.hasPlaylist || ch?.hasAudioSummary) ids.push(ch.id);
      });
    });

    return Array.from(new Set(ids));
  };

  rebuildAudioChaptersList = (references = []) => {
    if (!Array.isArray(references)) {
      this.setState({
        audioChapters: [],
        openRefIds: {},
      });
      return;
    }

    const audioChapters = [];
    references.forEach((ref) => {
      (ref.chapters || []).forEach((ch) => {
        if (ch?.hasPlaylist || ch?.hasAudioSummary) {
          audioChapters.push({
            ...ch,
            referenceId: ref.id,
            referenceName: ref.description
              ? `${ref.name} - ${ref.description}`
              : ref.name,
            actAsParent: ref.actAsParent,
          });
        }
      });
    });

    const firstRefId = audioChapters[0]?.referenceId;

    this.setState({
      audioChapters,
      openRefIds: firstRefId ? { [firstRefId]: true } : {},
    });
  };

  groupChaptersByReference = () => {
    const { audioChapters, audioTypeFilter } = this.state;

    const filtered = audioChapters.filter((ch) => {
      if (audioTypeFilter === 'qa') return ch.hasPlaylist;
      if (audioTypeFilter === 'summary') return ch.hasAudioSummary;
      if (audioTypeFilter === 'both')
        return ch.hasPlaylist && ch.hasAudioSummary;
      return false; // fallback, should never reach here
    });

    const map = new Map();
    filtered.forEach((ch) => {
      const refId = ch.referenceId;
      if (!map.has(refId)) {
        map.set(refId, {
          referenceId: refId,
          referenceName: ch.referenceName,
          actAsParent: ch.actAsParent,
          chapters: [],
        });
      }
      map.get(refId).chapters.push(ch);
    });

    return Array.from(map.values());
  };

  toggleReference = (referenceId) => {
    this.setState((prev) => ({
      openRefIds: {
        ...prev.openRefIds,
        [referenceId]: !prev.openRefIds[referenceId],
      },
    }));
  };

  openAudioPlayer = (chapterId, chapterName) => {
    this.props.getAudioPlaylist(chapterId);
    this.setState({
      isOpenAudioPlayer: true,
      chapterNameForAudioPlaylist: chapterName,
    });
  };

  closeAudioPlayer = () => {
    this.setState({
      isOpenAudioPlayer: false,
      customPlaylistChapters: null,
    });
  };

  // Returns the active chapters list based on context:
  // custom playlist chapters when playing a custom playlist, otherwise all audio chapters
  getActiveChaptersList = () =>
    this.state.customPlaylistChapters || this.state.audioChapters;

  getPrevChapter = (currentChapterId) => {
    const chapters = this.getActiveChaptersList();
    const idx = chapters.findIndex((c) => c.id === currentChapterId);
    return chapters[idx - 1] || null;
  };

  getNextChapter = (currentChapterId) => {
    const chapters = this.getActiveChaptersList();
    const idx = chapters.findIndex((c) => c.id === currentChapterId);
    return chapters[idx + 1] || null;
  };

  isFirstPlaylist = (currentChapterId) =>
    !this.getPrevChapter(currentChapterId);

  isLastPlaylist = (currentChapterId) => !this.getNextChapter(currentChapterId);

  getPreviousPlaylist = (currentChapterId) => {
    const prev = this.getPrevChapter(currentChapterId);
    if (!prev) return;

    this.props.getAudioPlaylist(prev.id);
    this.setState({ chapterNameForAudioPlaylist: prev.name });
  };

  getNextPlaylist = (currentChapterId) => {
    const next = this.getNextChapter(currentChapterId);
    if (!next) return;

    this.props.getAudioPlaylist(next.id);
    this.setState({ chapterNameForAudioPlaylist: next.name });
  };

  handleRestart = (chapterId) => {
    this.props.updateAudioPlaylistProgress(
      chapterId,
      INTRO_AUDIO_IDX,
      'PENDING',
      true,
    );

    // force refresh
    setTimeout(() => {
      this.props.getAudioPlaylistProgress([chapterId]);
    }, 300);
  };

  renderList = () => {
    const { hasLoadedAudioOnce, audioTypeFilter } = this.state;

    if (!hasLoadedAudioOnce) return null;

    const groups = this.groupChaptersByReference();

    if (!groups.length) {
      let message;

      if (audioTypeFilter === 'qa') {
        message = 'No references have Q&A only audio content.';
      } else if (audioTypeFilter === 'summary') {
        message = 'No references have Summary only audio content.';
      } else if (audioTypeFilter === 'both') {
        message = 'No references have both Q&A and Summary audio content.';
      } else {
        message = 'No audio content available for these references.';
      }

      return <div className="audio-study-empty">{message}</div>;
    }

    return (
      <div className="audio-study-list">
        {groups.map((g) => {
          const isOpen = !!this.state.openRefIds[g.referenceId];

          // has children chapters -> dropdown
          if (g.actAsParent) {
            return (
              <div key={g.referenceId} className="audio-study-ref">
                <button
                  type="button"
                  className="audio-study-ref__header"
                  onClick={() => this.toggleReference(g.referenceId)}
                >
                  <div className="audio-study-ref__left">
                    <i
                      className={`icon-expand rotate ${isOpen ? 'down' : ''}`}
                    />
                    <div className="audio-study-ref__title">
                      {g.referenceName}
                    </div>
                  </div>

                  <div className="audio-study-ref__meta">
                    <span className="audio-study-ref__chapters">
                      {g.chapters.length} Chapters
                    </span>
                  </div>
                </button>

                <Collapse isOpen={isOpen}>
                  <div className="audio-study-ref__body">
                    {g.chapters.map((ch) => {
                      const progressData =
                        this.props.audioPlaylistsProgress?.[ch.id];
                      let progressPct = 0;

                      if (progressData && progressData.total > 0) {
                        progressPct = Math.round(
                          (progressData.accumulated / progressData.total) * 100,
                        );
                      }

                      return (
                        <div
                          key={ch.id}
                          className="audio-study-chapter"
                          role="button"
                          tabIndex={0}
                          onClick={() => this.openAudioPlayer(ch.id, ch.name)}
                          onKeyDown={(e) => {
                            if (e.key === 'Enter' || e.key === ' ') {
                              e.preventDefault();
                              this.openAudioPlayer(ch.id, ch.name);
                            }
                          }}
                        >
                          <div className="audio-study-chapter__info">
                            <div className="audio-study-chapter__title">
                              {ch.description
                                ? `${ch.name} - ${ch.description}`
                                : ch.name}
                            </div>
                          </div>

                          <div
                            className="audio-study-chapter__actions"
                            onClick={(e) => e.stopPropagation()}
                            onKeyDown={(e) => e.stopPropagation()}
                          >
                            <AudioBadge
                              progress={progressPct}
                              hasPlaylist={!!ch.hasPlaylist}
                              hasAudioSummary={!!ch.hasAudioSummary}
                              onClick={() =>
                                this.openAudioPlayer(ch.id, ch.name)
                              }
                              onRestart={() => this.handleRestart(ch.id)}
                            />
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </Collapse>
              </div>
            );
          }

          // Orphan Chapter -> direct audio icon in the header
          const ch = g.chapters[0];
          const progressData = this.props.audioPlaylistsProgress?.[ch.id];
          let progressPct = 0;
          if (progressData && progressData.total > 0) {
            progressPct = Math.round(
              (progressData.accumulated / progressData.total) * 100,
            );
          }

          return (
            <div key={g.referenceId} className="audio-study-ref">
              <div
                className="audio-study-ref__header"
                role="button"
                tabIndex={0}
                onClick={() => this.openAudioPlayer(ch.id, ch.name)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    this.openAudioPlayer(ch.id, ch.name);
                  }
                }}
              >
                <div className="audio-study-ref__left">
                  <div className="audio-study-ref__title">
                    {g.referenceName}
                  </div>
                </div>

                <div
                  className="audio-study-chapter__actions"
                  onClick={(e) => e.stopPropagation()}
                  onKeyDown={(e) => e.stopPropagation()}
                >
                  <AudioBadge
                    progress={progressPct}
                    hasPlaylist={!!ch.hasPlaylist}
                    hasAudioSummary={!!ch.hasAudioSummary}
                    onClick={() => this.openAudioPlayer(ch.id, ch.name)}
                    onRestart={() => this.handleRestart(ch.id)}
                  />
                </div>
              </div>
            </div>
          );
        })}
      </div>
    );
  };

  render() {
    const {
      loadingReferences,
      userSubscription,
      user,
      pfeSktId,
      currentPlaylist,
    } = this.props;

    const { isOpenAudioPlayer } = this.state;

    const activeTab = this.getActiveTab();
    const sktLocked = userSubscription
      ? !userSubscription.isOccupational || this.props.user?.rate?.isPfeOnly
      : false;

    const isInitializing = loadingReferences || !user || !pfeSktId;
    const isWaitingFirstResponse = !this.state.hasFetchedReferences;

    const playlistIterator = currentPlaylist
      ? {
          isFirst: () => this.isFirstPlaylist(currentPlaylist.chapter),
          getPrevious: () => this.getPreviousPlaylist(currentPlaylist.chapter),
          current: {
            ...currentPlaylist,
            title: this.state.chapterNameForAudioPlaylist,
          },
          getNext: () => this.getNextPlaylist(currentPlaylist.chapter),
          isLast: () => this.isLastPlaylist(currentPlaylist.chapter),
        }
      : null;

    return (
      <div className="audio-study-page-container">
        <Row className="audio-study-toggle m-0">
          <Col xs={12} className="d-flex justify-content-end p-0">
            <PfeSktSwitch
              activeTab={activeTab}
              onSwitch={this.handleTabSwitch}
              variant="toggle"
              size="md"
              sktLocked={sktLocked}
            />
          </Col>
        </Row>

        {(isInitializing || isWaitingFirstResponse) && (
          <CircularLoading className="loading-audio-study-view" />
        )}

        {!isInitializing && !isWaitingFirstResponse && (
          <div className="audio-study-page-container__header">
            {/* LEFT: title + create button + update success message */}
            <div className="audio-study-page-container__header-left">
              <h2 className="custom-playlist-section__title">
                My Custom Playlists
              </h2>
              <CreatePlaylistButton onClick={this.openCreatePlaylistModal} />
              {this.state.showUpdateSuccess && (
                <span className="custom-playlist-section__success-msg">
                  ✓ Playlist updated successfully!
                </span>
              )}
            </div>

            {/* RIGHT: audio type filters */}
            <AudioTypeSelector
              activeFilter={this.state.audioTypeFilter}
              onChange={(filter) => this.setState({ audioTypeFilter: filter })}
            />
          </div>
        )}

        {!isInitializing && !isWaitingFirstResponse && (
          <CustomPlaylistSection
            playlists={this.props.customPlaylists}
            audioChapters={this.state.audioChapters}
            audioTypeFilter={this.state.audioTypeFilter}
            audioPlaylistsProgress={this.props.audioPlaylistsProgress}
            highlightPSPlaylist={this.state.highlightPSPlaylist}
            onPlay={(chapters, chapter) => {
              const target = chapter || chapters[0];
              if (target) {
                this.setState({ customPlaylistChapters: chapters });
                this.openAudioPlayer(target.id, target.name);
              }
            }}
            onEdit={(playlist) => {
              this.setState({
                editingPlaylist: playlist,
                showCreatePlaylistModal: true,
              });
            }}
            onDelete={this.handleOpenDeleteModal}
            onRestart={(chapterId) => this.handleRestart(chapterId)}
          />
        )}

        {!isInitializing && !isWaitingFirstResponse && (
          <>
            <h2 className="audio-study-references-title">By Reference</h2>
            {this.renderList()}
          </>
        )}

        {/* Modals */}
        <ModalNoReferences
          isOpen={this.state.showNoReferencesModal}
          isClose={() => this.setState({ showNoReferencesModal: false })}
          user={user}
        />

        <DeletePlaylistModal
          isOpen={this.state.deleteModalOpen}
          onClose={this.handleCloseDeleteModal}
          onConfirm={this.handleConfirmDelete}
          playlistName={this.state.playlistToDelete?.name || ''}
          deleting={this.state.deletingPlaylist}
        />

        <CreatePlaylistModal
          isOpen={this.state.showCreatePlaylistModal}
          onClose={this.closeCreatePlaylistModal}
          onSuccess={this.handlePlaylistCreated}
          pfeSktId={pfeSktId}
          editingPlaylist={this.state.editingPlaylist}
          existingPlaylists={this.props.customPlaylists || []}
        />

        {isOpenAudioPlayer && playlistIterator && (
          <AudioPlayer
            playlistIterator={playlistIterator}
            open={isOpenAudioPlayer}
            close={this.closeAudioPlayer}
            updateAudioPlaylistProgress={this.props.updateAudioPlaylistProgress}
            audioPlaylistsProgress={this.props.audioPlaylistsProgress}
          />
        )}
      </div>
    );
  }
}

export default AudioStudyView;
